// src/contexts/RegionContext.tsx (ensure file extension is .tsx)

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { RegionContextType } from "../interfaces/UserInfo";
import { useAuth } from "../firebase/AuthProvider";

// Create Context with Type and Defaults 
const defaultContextValue: RegionContextType = {
  availableRegions: [],
  selectedRegionId: null,
  setSelectedRegionId: () => {}, 
};

// Create the context with the specific type
const RegionContext = createContext<RegionContextType>(defaultContextValue);

// Create Provider Component 
interface RegionProviderProps {
  children: ReactNode; 
}

export const RegionProvider: React.FC<RegionProviderProps> = ({ children }) => {
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const { currentUserInfo } = useAuth();

  const availableRegions = useMemo(
    () => currentUserInfo?.regions || [],
    [currentUserInfo?.regions]
  );

  useEffect(() => {
    const lastSelectedRegionIdString = localStorage.getItem("selectedRegionId");
    let initialRegionId: string | null = null; 

    if (availableRegions.length > 0) {
      // Check if there's a stored ID
      if (lastSelectedRegionIdString !== null) {
        const lastSelectedId = lastSelectedRegionIdString;
        // Check if the stored region ID is valid for the current user's regions
        const isValidStoredRegion =
          lastSelectedId &&
          availableRegions.some((r) => r.regionName === lastSelectedId);
        if (isValidStoredRegion) {
          initialRegionId = lastSelectedId;
        }
      }

      // If no valid stored region was found, default to the first available one
      if (initialRegionId === null) {
        initialRegionId = availableRegions[0]?.regionName; 
      }
    } else {
      // No regions available for this user
      initialRegionId = null;
    }

    setSelectedRegionId(initialRegionId);
  }, [availableRegions]); // Re-run when the user's available regions change

  useEffect(() => {
    if (selectedRegionId !== null) {
      localStorage.setItem("selectedRegionId", selectedRegionId.toString());
    } else {
      // Remove the item if no region is selected (or user logs out/has no regions)
      localStorage.removeItem("selectedRegionId");
    }
  }, [selectedRegionId]);

  const contextValue = useMemo<RegionContextType>(
    () => ({
      availableRegions,
      selectedRegionId,
      setSelectedRegionId, 
    }),
    [availableRegions, selectedRegionId, setSelectedRegionId]
  ); 

  return (
    <RegionContext.Provider value={contextValue}>
      {children}
    </RegionContext.Provider>
  );
};


// Hook
export const useSelectedRegion = (): RegionContextType => {
  const context = useContext(RegionContext);
  if (
    context === defaultContextValue ||
    context.setSelectedRegionId === defaultContextValue.setSelectedRegionId
  ) {
    console.warn(
      "useSelectedRegion seems to be used outside of a RegionProvider or before it has initialized."
    );
    throw new Error("useSelectedRegion must be used within a RegionProvider");
  }
  return context;
};
